/**
 * Tool Settings Panel
 * Router component that displays the appropriate settings for the active tool
 */

import React from 'react';
import type { ToolId } from '@/types/tools';
import { TextToolSettings } from './TextToolSettings';
import { ShapeToolSettings } from './ShapeToolSettings';
import { LineToolSettings } from './LineToolSettings';
import { ResourceToolSettings } from './ResourceToolSettings';

interface ToolSettingsProps {
  toolId: ToolId;
  orientation: 'vertical' | 'horizontal';
}

const TOOL_TITLES: Partial<Record<ToolId, string>> = {
  text: 'Text Settings',
  shape: 'Shape Settings',
  line: 'Line Settings',
  resource: 'Resources',
};

export function ToolSettings({ toolId, orientation }: ToolSettingsProps) {
  const title = TOOL_TITLES[toolId];
  if (!title) return null;

  // Render appropriate settings component
  const renderSettings = () => {
    switch (toolId) {
      case 'text':
        return <TextToolSettings />;
      case 'shape':
        return <ShapeToolSettings />;
      case 'line':
        return <LineToolSettings />;
      case 'resource':
        return <ResourceToolSettings />;
      default:
        return null;
    }
  };

  const isVertical = orientation === 'vertical';

  // Resource panel is wider to accommodate the grid
  const width = toolId === 'resource' ? 'w-64' : 'w-52';

  return (
    <div
      className={`absolute bg-navy-light/90 backdrop-blur-xl border border-white/10 rounded-xl shadow-xl ${width} p-3 overflow-hidden`}
      style={isVertical
        ? { top: '100%', left: 0, marginTop: 8 }
        : { left: '100%', top: 0, marginLeft: 8 }
      }
    >
      <h3 className="text-xs font-medium text-fg mb-3">{title}</h3>
      {renderSettings()}
    </div>
  );
}
